/**
 * VÉRAULT Member Suite — FormResponseModal (Wave 27.5 · Phase 4e).
 * ────────────────────────────────────────────────────────────────────
 * In-portal form/intake renderer. Fetches packet via GET /forms/{id},
 * shows a calm scrollable column of questions, submits to
 * POST /forms/{id}/respond.
 *
 *   Question kinds: text · longtext · choice. Default schema for legacy
 *   packets (no questions stored): dietary + allergies + mobility +
 *   celebrating prompts.
 */
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clipboard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../../index";
import { useOnlineState } from "../../../../components/OnlineProvider";

export default function FormResponseModal({ packet, onClose, onSubmitted }) {
  const [questions, setQuestions] = useState(null);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const { isOnline } = useOnlineState();

  useEffect(() => {
    if (!packet?.id) return;
    api.get(`/client-portal/forms/${packet.id}`)
      .then((res) => setQuestions(res.data.questions || []))
      .catch(() => setLoadError(true));
  }, [packet?.id]);

  const handleSubmit = async () => {
    const filled = Object.entries(responses).filter(([, v]) => (v || "").trim()).length;
    if (filled === 0) {
      toast.error("Please answer at least one question.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/client-portal/forms/${packet.id}/respond`, { responses });
      toast.success("Form submitted", {
        description: "Your advisor has been notified.",
      });
      onSubmitted?.(res.data);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't submit the form.";
      toast.error("Submission failed", { description: detail });
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-50 bg-black/65 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
        onClick={onClose}
        data-testid="form-response-modal"
      >
        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.35 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col rounded-lg bg-[hsl(var(--background))] shadow-2xl"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 inline-flex items-center justify-center size-9 rounded-full hover:bg-[hsl(var(--background-paper))] z-10"
          >
            <X className="size-4" strokeWidth={1.5} />
          </button>

          <div className="px-7 sm:px-10 py-7 border-b border-[rgba(0,0,0,0.05)] flex-shrink-0">
            <div className="flex items-center gap-2.5 mb-2">
              <Clipboard className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
                Traveler form
              </p>
            </div>
            <h2 className="font-serif text-[22px] leading-tight tracking-tight">
              {packet.title || "Tell us a little more"}
            </h2>
            <p className="mt-2 font-accent italic text-[13.5px] text-[hsl(var(--foreground-muted))]">
              These notes go straight to your advisor — share what's
              useful, skip what isn't.
            </p>
          </div>

          <div className="px-7 sm:px-10 py-6 overflow-y-auto flex-1 space-y-6">
            {loadError && (
              <p className="font-accent italic text-[13.5px] text-[hsl(var(--foreground-muted))]" data-testid="form-load-error">
                Couldn't load the form. Please try again later.
              </p>
            )}
            {!questions && !loadError && (
              <div className="flex justify-center py-6">
                <Loader2 className="size-5 animate-spin text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
              </div>
            )}
            {questions && questions.map((q) => (
              <div key={q.id} data-testid={`form-question-${q.id}`}>
                <label className="block font-serif text-[15px] text-[hsl(var(--foreground))] mb-2">
                  {q.label}
                </label>
                {q.kind === "longtext" ? (
                  <textarea
                    rows={3}
                    value={responses[q.id] || ""}
                    onChange={(e) => setResponses((r) => ({ ...r, [q.id]: e.target.value }))}
                    placeholder="—"
                    className="w-full px-4 py-3 rounded-md border border-[hsl(var(--border))] bg-white/60 font-accent italic text-[14px] placeholder:text-[hsl(var(--foreground-muted))] focus:border-[hsl(var(--accent-gold))] focus:outline-none transition-colors resize-none"
                    data-testid={`form-input-${q.id}`}
                  />
                ) : (
                  <input
                    type="text"
                    value={responses[q.id] || ""}
                    onChange={(e) => setResponses((r) => ({ ...r, [q.id]: e.target.value }))}
                    placeholder="—"
                    className="w-full px-4 py-2.5 rounded-md border border-[hsl(var(--border))] bg-white/60 font-accent italic text-[14px] placeholder:text-[hsl(var(--foreground-muted))] focus:border-[hsl(var(--accent-gold))] focus:outline-none transition-colors"
                    data-testid={`form-input-${q.id}`}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="px-7 sm:px-10 py-5 border-t border-[rgba(0,0,0,0.05)] flex items-center justify-end gap-2 flex-shrink-0">
            {!isOnline ? (
              <span
                className="mr-auto text-[11px] italic text-[hsl(var(--foreground-muted))]"
                data-testid="form-offline-hint"
              >
                Available when you reconnect.
              </span>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || !questions || questions.length === 0 || !isOnline}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-mono text-[10px] uppercase tracking-[0.22em] bg-[hsl(var(--foreground))] text-[hsl(var(--background))] disabled:opacity-40 hover:bg-[hsl(var(--accent-gold))] transition-colors"
              data-testid="form-submit"
            >
              {submitting && <Loader2 className="size-3 animate-spin" strokeWidth={2} />}
              Submit form
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
